import React, { useEffect, useState, useRef, useCallback } from "react";
import { RefreshCw, Search, Clock3 } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Card, Empty } from "@/components/ui/page";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtClock, fmtDuration, fmtTime } from "@/lib/format";

export default function LiveMonitor() {
  const [rows, setRows] = useState([]);
  const [updated, setUpdated] = useState(null);
  const [auto, setAuto] = useState(true);
  const [q, setQ] = useState("");
  const [proc, setProc] = useState("");
  const [status, setStatus] = useState("");
  const [tick, setTick] = useState(0);
  const baseRef = useRef({ at: Date.now(), rows: [] });

  const load = useCallback(() => {
    api.get("/monitor").then((r) => {
      baseRef.current = { at: Date.now(), rows: r.data.rows };
      setRows(r.data.rows);
      setUpdated(new Date(r.data.last_updated));
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!auto) return;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [auto, load]);
  // per-second local tick to advance timers
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const elapsedSince = Math.floor((Date.now() - baseRef.current.at) / 1000);
  const processes = [...new Set(rows.map((r) => r.process).filter(Boolean))];
  const filtered = rows.filter((r) => {
    if (q && !`${r.name} ${r.employee_id}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (proc && r.process !== proc) return false;
    if (status && r.status !== status) return false;
    return true;
  });

  return (
    <div>
      <PageHeader title="Live Workforce Monitor" subtitle="Real-time employee status across the floor" testId="monitor-header">
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500">
          <Clock3 className="h-3.5 w-3.5" /> Updated {updated ? updated.toLocaleTimeString("en-IN", { hour12: false }) : "—"}
        </div>
        <button data-testid="monitor-auto" onClick={() => setAuto((a) => !a)} className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold ${auto ? "bg-teal-50 text-teal-700" : "bg-slate-100 text-slate-500"}`}>
          <span className={`h-2 w-2 rounded-full ${auto ? "bg-[#08A7A0] gs-live-dot" : "bg-slate-400"}`} /> Auto {auto ? "ON" : "OFF"}
        </button>
        <button data-testid="monitor-refresh" onClick={load} className="flex items-center gap-2 rounded-lg bg-[#0B1F33] px-3 py-2 text-xs font-semibold text-white"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
      </PageHeader>

      <Card className="mb-4 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input data-testid="monitor-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employee or ID…" className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-[#08A7A0]" />
          </div>
          <select data-testid="monitor-process" value={proc} onChange={(e) => setProc(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#08A7A0]">
            <option value="">All Processes</option>
            {processes.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select data-testid="monitor-status" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#08A7A0]">
            <option value="">All Status</option>
            <option value="working">Working</option>
            <option value="on_break">On Break</option>
            <option value="excess">Excess Break</option>
            <option value="meeting">Meeting</option>
            <option value="away">Away</option>
            <option value="offline">Offline</option>
          </select>
        </div>
      </Card>

      <Card testId="monitor-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3">Employee</th>
                <th className="px-4 py-3">Process</th>
                <th className="px-4 py-3">Shift</th>
                <th className="px-4 py-3">Login</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Break Type</th>
                <th className="px-4 py-3">Current Timer</th>
                <th className="px-4 py-3">Today's Break</th>
                <th className="px-4 py-3">Exception</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={9}><Empty /></td></tr>}
              {filtered.map((r) => {
                const liveTimer = r.timer_seconds > 0 ? r.timer_seconds + elapsedSince : 0;
                return (
                  <tr key={r.id} data-testid={`monitor-row-${r.employee_id}`} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[#0B1F33]">{r.name}</div>
                      <div className="text-xs text-slate-400">{r.employee_id} · {r.designation}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{r.process}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{r.shift}</td>
                    <td className="px-4 py-3 font-mono text-xs">{fmtTime(r.login_time)}</td>
                    <td className="px-4 py-3"><StatusBadge status={r.status} testId={`monitor-status-${r.employee_id}`} /></td>
                    <td className="px-4 py-3 text-slate-600">{r.break_type || "—"}</td>
                    <td className="px-4 py-3">
                      {liveTimer > 0 ? <span className={`font-mono font-bold ${r.status === "excess" ? "text-red-600" : "text-[#0B1F33]"}`}>{fmtClock(liveTimer)}</span> : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">{fmtDuration(r.today_break_seconds)}</td>
                    <td className="px-4 py-3">{r.exception ? <span className="rounded-full bg-red-50 px-2 py-1 text-xs font-bold text-red-600">RED ALERT</span> : <span className="text-slate-300">—</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
